import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Search, FileDown, Star } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AddToCartControl from "@/components/AddToCartControl";
import { SkeletonLine } from "@/components/Skeleton";
import BackToTop from "@/components/BackToTop";
import { useAuth } from "@/contexts/AuthContext";

function formatPrice(item) {
    if (item.prix_kg_ht == null || item.prix_kg_ht === "") return "Sur demande";
    const value = Number(item.prix_kg_ht).toFixed(2);
    return `CHF ${value} / ${item.unite || "kg"}`;
}

function ExclusiveBadge({ compact = false }) {
    return (
        <span
            className={`inline-flex items-center gap-1 uppercase tracking-widest border ${compact ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5"}`}
            style={{ color: "#D4BC7C", borderColor: "var(--bc-gold)", background: "rgba(212,188,124,0.08)" }}
            data-testid="badge-selected-for-you"
            title="Article proposé spécialement à votre établissement"
        >
            <Star size={compact ? 9 : 10} strokeWidth={2.5} className="fill-[#D4BC7C]" />
            Sélectionné pour vous
        </span>
    );
}

export default function Tarifs() {
    const { user } = useAuth();
    const [search, setSearch] = useState("");
    const [selectedCat, setSelectedCat] = useState("all");

    const { data: categories = [] } = useQuery({
        queryKey: ["categories"],
        queryFn: async () => (await api.get("/categories")).data,
    });
    const { data: items = [], isLoading } = useQuery({
        queryKey: ["prices"],
        queryFn: async () => (await api.get("/prices")).data,
    });

    const catById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

    const normalize = (s) =>
        (s || "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/œ/g, "oe")
            .replace(/æ/g, "ae");

    const filtered = useMemo(() => {
        const q = normalize(search.trim());
        return items
            .filter((it) => (selectedCat === "all" ? true : it.category_id === selectedCat))
            .filter((it) => {
                if (!q) return true;
                const hay = normalize(`${it.article} ${it.code} ${it.provenance}`);
                return hay.includes(q);
            })
            .sort((a, b) => (a.code || "").localeCompare(b.code || ""));
    }, [items, search, selectedCat]);

    const grouped = useMemo(() => {
        const g = {};
        for (const it of filtered) {
            const cname = catById[it.category_id]?.nom || "Autres";
            g[cname] = g[cname] || [];
            g[cname].push(it);
        }
        const order = categories.map((c) => c.nom);
        return Object.entries(g).sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
    }, [filtered, catById, categories]);

    const loadImageAsDataUrl = (url) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
            img.onerror = reject;
            img.src = url;
        });

    const downloadPdf = async () => {
        try {
            const doc = new jsPDF({ unit: "pt", format: "a4" });
            const now = new Date().toLocaleDateString("fr-CH");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(20);
            doc.setTextColor(212, 188, 124);
            doc.text("BOUCHERIE COSTA — Tarifs Pro", 40, 50);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text(`Yverdon-les-Bains · Édité le ${now} · Prix CHF, hors TVA · Délai de commande : 48h`, 40, 68);

            let startY = 90;
            grouped.forEach(([cat, rows]) => {
                // Section title (category name) above the table
                if (startY > 720) {
                    doc.addPage();
                    startY = 40;
                }
                doc.setFont("helvetica", "bold");
                doc.setFontSize(13);
                doc.setTextColor(212, 188, 124);
                doc.text(cat, 40, startY);
                // Thin gold hairline under the title
                doc.setDrawColor(212, 188, 124);
                doc.setLineWidth(0.5);
                doc.line(40, startY + 4, 555, startY + 4);
                startY += 14;

                autoTable(doc, {
                    startY,
                    head: [["Article", "Code", "Provenance", "Prix HT"]],
                    body: rows.map((r) => [
                        r.article,
                        r.code || "-",
                        r.provenance || "-",
                        r.prix_kg_ht == null
                            ? "Sur demande"
                            : `CHF ${Number(r.prix_kg_ht).toFixed(2)} / ${r.unite || "kg"}`,
                    ]),
                    styles: { font: "helvetica", fontSize: 9, textColor: 40, cellPadding: 4 },
                    headStyles: { fillColor: [0, 0, 0], textColor: [212, 188, 124], fontStyle: "bold" },
                    alternateRowStyles: { fillColor: [245, 240, 230] },
                    margin: { left: 40, right: 40 },
                });
                startY = doc.lastAutoTable.finalY + 24;
                if (startY > 720) {
                    doc.addPage();
                    startY = 40;
                }
            });

            // QR code footer: "Passer commande en deux clics"
            try {
                const qrData = await loadImageAsDataUrl("/qr-costa.png");
                const qrSize = 110;
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                // Reserve space; add new page if not enough room
                if (startY + qrSize + 60 > pageHeight - 40) {
                    doc.addPage();
                    startY = 60;
                }
                const qrX = (pageWidth - qrSize) / 2;
                const qrY = startY + 10;
                doc.addImage(qrData, "PNG", qrX, qrY, qrSize, qrSize);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.setTextColor(212, 188, 124);
                doc.text("Passer commande en deux clics", pageWidth / 2, qrY + qrSize + 20, { align: "center" });
                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
                doc.setTextColor(120, 120, 120);
                doc.text("Scannez avec votre téléphone pour accéder à votre Espace Pro", pageWidth / 2, qrY + qrSize + 34, { align: "center" });
            } catch (imgErr) {
                // If QR fails to load, do not block the export
                console.warn("QR code non chargé:", imgErr);
            }

            doc.save(`Boucherie-Costa-Tarifs-Pro-${now.replaceAll("/", "-")}.pdf`);
            toast.success("PDF téléchargé");
        } catch (e) {
            toast.error("Impossible de générer le PDF");
        }
    };

    return (
        <section className="max-w-7xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="tarifs-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                <div>
                    <p className="bc-label-small">Espace Pro</p>
                    <h1 className="font-serif text-[#D4BC7C] text-4xl sm:text-5xl mt-4">Tarifs professionnels</h1>
                    <div className="bc-hairline-gold my-6" />
                    <p className="text-sm text-[#F2ECE0]/80 max-w-xl">
                        <span className="text-[#D4BC7C]">Prix en CHF, hors TVA.</span> Délai de commande : 48h.
                        Nos prix sont susceptibles d&apos;évoluer selon les cours du marché.
                    </p>
                </div>
                <button className="bc-btn" onClick={downloadPdf} data-testid="btn-download-pdf">
                    <FileDown size={16} strokeWidth={2} /> Télécharger en PDF
                </button>
            </div>

            {/* Filters (sticky on scroll) */}
            <div className="mt-10 bc-tarifs-filters" data-testid="tarifs-filters">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4BC7C] pointer-events-none z-10" size={18} />
                        <input
                            className="bc-input"
                            style={{ paddingLeft: "3rem" }}
                            placeholder="Rechercher un article, un code, une provenance…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="tarifs-search"
                        />
                    </div>
                    <select
                        className="bc-input md:w-72"
                        value={selectedCat}
                        onChange={(e) => setSelectedCat(e.target.value)}
                        data-testid="tarifs-category"
                    >
                        <option value="all">Toutes les catégories</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.nom}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="mt-12">
                {isLoading ? (
                    <div className="space-y-6" data-testid="tarifs-loading">
                        <SkeletonLine width="30%" height={24} />
                        <div className="space-y-3 mt-6">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="flex gap-4 items-center">
                                    <SkeletonLine width="8%" height={14} />
                                    <SkeletonLine width="42%" height={14} />
                                    <SkeletonLine width="18%" height={14} />
                                    <SkeletonLine width="14%" height={14} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <p className="text-[#A0A0A0]" data-testid="tarifs-empty">Aucun article ne correspond à votre recherche.</p>
                ) : (
                    grouped.map(([catName, rows]) => (
                        <div key={catName} className="mb-14 bc-anim-in" data-testid={`tarifs-cat-${catName}`}>
                            <div className="flex items-center gap-4 mb-4">
                                <h2 className="font-serif text-[#D4BC7C] text-2xl md:text-3xl">{catName}</h2>
                                <span className="text-xs text-[#A0A0A0] tracking-widest uppercase">{rows.length} articles</span>
                            </div>
                            <div className="bc-hairline mb-6" />

                            {/* Desktop: table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="bc-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "10%" }}>Code</th>
                                            <th style={{ width: "38%" }}>Article</th>
                                            <th style={{ width: "16%" }}>Provenance</th>
                                            <th style={{ width: "16%" }}>Prix HT</th>
                                            <th style={{ width: "20%" }}>Panier</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => {
                                            const isExclusive =
                                                user?.role !== "admin" &&
                                                Array.isArray(r.target_user_ids) &&
                                                r.target_user_ids.length > 0 &&
                                                r.target_user_ids.includes(user?.id);
                                            return (
                                                <tr key={r.id} data-testid={`tarifs-row-${r.code}`}>
                                                    <td className="text-[#A0A0A0] font-mono text-xs">{r.code || "—"}</td>
                                                    <td>
                                                        <div className="flex items-start gap-2 flex-wrap">
                                                            <span>{r.article}</span>
                                                            {isExclusive && <ExclusiveBadge compact />}
                                                        </div>
                                                    </td>
                                                    <td className="text-[#A0A0A0]">{r.provenance || "—"}</td>
                                                    <td className="text-[#D4BC7C] font-medium">{formatPrice(r)}</td>
                                                    <td>
                                                        <AddToCartControl item={r} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile: cards */}
                            <div className="md:hidden space-y-3">
                                {rows.map((r) => {
                                    const isExclusive =
                                        user?.role !== "admin" &&
                                        Array.isArray(r.target_user_ids) &&
                                        r.target_user_ids.length > 0 &&
                                        r.target_user_ids.includes(user?.id);
                                    return (
                                        <div key={r.id} className="bc-card p-4" data-testid={`tarifs-card-${r.code}`}>
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="text-[10px] text-[#A0A0A0] tracking-widest uppercase">
                                                            {r.code || "—"}
                                                        </p>
                                                        {isExclusive && <ExclusiveBadge compact />}
                                                    </div>
                                                    <p className="text-[#F2ECE0] text-sm mt-1 leading-snug">{r.article}</p>
                                                    {r.provenance && (
                                                        <p className="text-xs text-[#A0A0A0] mt-2">{r.provenance}</p>
                                                    )}
                                                </div>
                                                <div className="text-[#D4BC7C] text-sm font-medium whitespace-nowrap text-right">
                                                    {formatPrice(r)}
                                                </div>
                                            </div>
                                            <div className="mt-3 pt-3 border-t flex justify-end" style={{ borderColor: "var(--bc-border)" }}>
                                                <AddToCartControl item={r} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))
                )}
            </div>
            <BackToTop threshold={500} />
        </section>
    );
}
